import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

export const Route = createFileRoute("/_authenticated/_account/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications — CoderLity" },
      { name: "description", content: "Choose which emails you receive." },
    ],
  }),
  component: NotificationsPage,
});

type Prefs = { product_updates: boolean; marketing: boolean; security_alerts: boolean };
const defaults: Prefs = { product_updates: true, marketing: false, security_alerts: true };

const items: { key: keyof Prefs; title: string; desc: string }[] = [
  { key: "product_updates", title: "Product updates", desc: "New features, improvements, and changes." },
  { key: "marketing", title: "Marketing emails", desc: "Tips, offers, and the occasional newsletter." },
  { key: "security_alerts", title: "Security alerts", desc: "Sign-ins and changes to your account." },
];

function NotificationsPage() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>(defaults);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("notification_prefs")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.notification_prefs) {
          setPrefs({ ...defaults, ...(data.notification_prefs as Partial<Prefs>) });
        }
        setLoading(false);
      });
  }, [user]);

  const toggle = async (key: keyof Prefs, value: boolean) => {
    if (!user) return;
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    const { error } = await supabase.from("profiles").update({ notification_prefs: next }).eq("id", user.id);
    if (error) {
      toast.error(error.message);
      setPrefs(prefs);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>Choose which emails you'd like to receive.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item, i) => (
          <div key={item.key}>
            {i > 0 && <Separator className="my-2" />}
            <div className="flex items-start justify-between gap-4 py-3">
              <div className="space-y-1">
                <Label htmlFor={item.key} className="text-base font-medium">{item.title}</Label>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
              <Switch
                id={item.key}
                checked={prefs[item.key]}
                disabled={loading}
                onCheckedChange={(v) => toggle(item.key, v)}
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
